import styled from '@emotion/styled';

import { Badge } from '@/components/common/Badge';
import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { RichHeading } from '@/components/common/Heading';
import { Image } from '@/components/common/image';
import { HeadingLevel } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Shadow } from '@/components/common/WistiaComponents/Shared';
import { WistiaInlinePlayer } from '@/components/common/WistiaComponents/WistiaInlinePlayer';
import { WistiaVideoPlayer } from '@/components/common/WistiaComponents/WistiaVideoPlayer';
import { Section } from '@/components/layout/Section';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { blockUrlWithAudience } from '@/lib/utils';

import type {
  Audience as SanityAudience,
  UspWithMedia as SanityUspWithMedia,
} from '@/sanity/sanity.types';
import type { BlockWithAudience, StandardImage } from '@/types/shared';

export interface USPWithMediaProps
  extends Omit<SanityUspWithMedia, 'CTA' | 'image'>,
    BlockWithAudience {
  image: StandardImage;
  CTA?: Omit<SanityUspWithMedia['CTA'], 'audienceRef'> & {
    audienceRef?: SanityAudience;
  };
}

export const USPWithMedia = ({
  tag,
  captionHeadingDescription,
  usps,
  image,
  wistiaPopover,
  CTA,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,

  pageAudience,
  isAudienceSwitcherEnabled,
}: USPWithMediaProps) => {
  const isDesktop = useMediaQuery(screen.mdQuery);
  const isVideo = !!wistiaPopover?.videoId || wistiaPopover?.videoTitle;
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColour?.token}
      className="usp-with-media"
    >
      <HeadingLevel>
        {tag?.tagText && (
          <Badge
            bgColour={tag?.tagColour}
            margin="0 0 var(--spacing-xxx-small) 0"
          >
            {tag.tagText}
          </Badge>
        )}
        {captionHeadingDescription && (
          <StyledCaptionHeadingDescription
            {...captionHeadingDescription}
            desktopAlignment="start"
            mobileAlignment="start"
            isCaptionUppercase={false}
            pageAudience={pageAudience}
            isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
          />
        )}
        <SplitColumn>
          <USPs>
            {usps.map((usp) => {
              return (
                <USP key={usp._key}>
                  {usp.heading && (
                    <HeadingLevel>
                      <RichHeading heading={usp.heading} size="heading-md" />
                    </HeadingLevel>
                  )}
                  <USPDescription>
                    <BulletPoints bulletPointStyle="hexagon">
                      <PortableText
                        value={usp.description}
                        audienceConfig={{
                          pageAudience,
                          isAudienceSwitcherEnabled,
                        }}
                      />
                    </BulletPoints>
                  </USPDescription>
                </USP>
              );
            })}
            {CTA && CTA.text && (
              <Button
                key={CTA.text}
                iconPosition="right"
                variant="solid-light"
                href={blockUrlWithAudience(
                  CTA.url,
                  CTA.audienceRef,
                  pageAudience,
                  CTA.noAudienceToggle,
                  isAudienceSwitcherEnabled,
                )}
                aria-label={CTA.accessibleLabel}
                className={
                  CTA?.trackingTag?.blockName &&
                  `tracking-${CTA.trackingTag.blockName}__${CTA.trackingTag.blockAction}`
                }
              >
                {CTA.text}
              </Button>
            )}
          </USPs>
          {!isVideo ? (
            <ImageContainer>
              <Shadow />
              <Image {...image} objectFit="cover" radius="l" />
            </ImageContainer>
          ) : isDesktop ? (
            <WistiaInlinePlayer image={image} wistiaPopover={wistiaPopover} />
          ) : (
            <WistiaVideoPlayer image={image} wistiaPopover={wistiaPopover} />
          )}
        </SplitColumn>
      </HeadingLevel>
    </Section>
  );
};

const SplitColumn = styled.div`
  display: flex;
  flex-direction: column-reverse;

  ${screen.md} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-x-large);
  }
`;

const ImageContainer = styled.div`
  max-height: 350px;
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-450);
  > img,
  div {
    height: 100%;
  }
`;

const USP = styled.div``;
const USPs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: var(--spacing-x-large);
  gap: var(--spacing-large);

  ${screen.md} {
    align-items: flex-start;
    text-align: start;
    margin-top: 0;
    gap: var(--spacing-medium);
  }
`;

const USPDescription = styled.div`
  padding-top: var(--spacing-x-small);
`;

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-x-large);
`;
